// Keep this module free of Decimal imports: ordinary navigation must not load
// the deep engine (including its arithmetic library).
export const CAMERA_VERSION = 1;
export const DEEP_PIXEL_THRESHOLD = 2 ** -38;
export const DEEP_PREFETCH_FACTOR = 16;
export const DEEP_EXIT_FACTOR = 4;

export function assertPositive(value, name) {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} must be finite and positive`);
}

export function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
}

export function validateCameraSnapshot(snapshot) {
  if (!snapshot || snapshot.version !== CAMERA_VERSION) throw new TypeError('Unsupported camera snapshot');
  for (const key of ['centerX', 'centerY', 'spanY']) {
    if (typeof snapshot[key] !== 'string' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(snapshot[key])) {
      throw new TypeError(`Camera ${key} must be a decimal string`);
    }
  }
  assertPositive(snapshot.aspect, 'Camera aspect');
  if (!Number.isInteger(snapshot.precisionDigits) || snapshot.precisionDigits < 1) {
    throw new RangeError('Camera precisionDigits must be a positive integer');
  }
}

export class NumberCamera {
  constructor(centerX, centerY, spanY, aspect) {
    assertFinite(centerX, 'Camera centerX');
    assertFinite(centerY, 'Camera centerY');
    assertPositive(spanY, 'Camera spanY');
    assertPositive(aspect, 'Camera aspect');
    this.kind = 'number';
    this.centerX = centerX;
    this.centerY = centerY;
    this.spanY = spanY;
    this.aspect = aspect;
    Object.freeze(this);
  }

  snapshot() {
    return Object.freeze({
      version: CAMERA_VERSION, centerX: String(this.centerX), centerY: String(this.centerY),
      spanY: String(this.spanY), aspect: this.aspect, precisionDigits: 16
    });
  }

  zoomAt(factor, u, v) {
    assertPositive(factor, 'Zoom factor');
    assertFinite(u, 'Pointer u');
    assertFinite(v, 'Pointer v');
    const nextSpan = this.spanY * factor;
    return new NumberCamera(
      this.centerX + (u - 0.5) * this.spanY * this.aspect * (1 - factor),
      this.centerY + (v - 0.5) * this.spanY * (1 - factor), nextSpan, this.aspect
    );
  }

  panPixels(dx, dy, cssWidth, cssHeight) {
    assertFinite(dx, 'Pan dx');
    assertFinite(dy, 'Pan dy');
    assertPositive(cssWidth, 'CSS width');
    assertPositive(cssHeight, 'CSS height');
    return new NumberCamera(
      this.centerX + dx / cssWidth * this.spanY * this.aspect,
      this.centerY + dy / cssHeight * this.spanY, this.spanY, this.aspect
    );
  }

  fitAspect(aspect) {
    return new NumberCamera(this.centerX, this.centerY, this.spanY, aspect);
  }

  toShallowRanges() {
    const halfX = this.spanY * this.aspect / 2;
    const halfY = this.spanY / 2;
    return { xRange: [this.centerX - halfX, this.centerX + halfX], yRange: [this.centerY - halfY, this.centerY + halfY] };
  }

  pixelSpacing(width = 1600, height = 1200) {
    assertPositive(width, 'Render width');
    assertPositive(height, 'Render height');
    return Math.min(this.spanY * this.aspect / width, this.spanY / height);
  }

  needsDeep(width = 1600, height = 1200, wasDeep = false) {
    const threshold = Math.max(1, Math.abs(this.centerX), Math.abs(this.centerY)) * DEEP_PIXEL_THRESHOLD;
    const spacing = this.pixelSpacing(width, height);
    return wasDeep ? spacing < DEEP_EXIT_FACTOR * threshold : spacing <= threshold;
  }

  shouldPrefetch(width = 1600, height = 1200) {
    const threshold = Math.max(1, Math.abs(this.centerX), Math.abs(this.centerY)) * DEEP_PIXEL_THRESHOLD;
    return this.pixelSpacing(width, height) <= DEEP_PREFETCH_FACTOR * threshold;
  }

  viewTransform(source) {
    validateCameraSnapshot(source);
    const sourceY = Number(source.spanY);
    const sourceX = sourceY * source.aspect;
    const scale = [this.spanY * this.aspect / sourceX, this.spanY / sourceY];
    return {
      scale,
      offset: [0.5 + (this.centerX - Number(source.centerX)) / sourceX - 0.5 * scale[0],
        0.5 + (this.centerY - Number(source.centerY)) / sourceY - 0.5 * scale[1]]
    };
  }

  pixelOffsetFromReference(u, v, reference) {
    assertFinite(u, 'Pixel u');
    assertFinite(v, 'Pixel v');
    return {
      x: String(this.centerX - Number(reference.centerX) + (u - 0.5) * this.spanY * this.aspect),
      y: String(this.centerY - Number(reference.centerY) + (v - 0.5) * this.spanY)
    };
  }
}

export function cameraFromRanges(xRange, yRange) {
  if (!Array.isArray(xRange) || !Array.isArray(yRange) || xRange.length !== 2 || yRange.length !== 2) {
    throw new TypeError('Camera ranges must each contain two endpoints');
  }
  const spanX = xRange[1] - xRange[0];
  const spanY = yRange[1] - yRange[0];
  assertPositive(spanX, 'Horizontal span');
  assertPositive(spanY, 'Vertical span');
  return new NumberCamera(xRange[0] + spanX / 2, yRange[0] + spanY / 2, spanY, spanX / spanY);
}

export function numberCameraFromSnapshot(snapshot) {
  validateCameraSnapshot(snapshot);
  return new NumberCamera(Number(snapshot.centerX), Number(snapshot.centerY), Number(snapshot.spanY), snapshot.aspect);
}
